import { IS_STATIC } from '../api/client'

/**
 * The one translation between the platform's paths and this application's.
 *
 * The server speaks in /app/... paths: they are catalogue rows in
 * rbac.section.route, the row-scope predicates are declared against them, and
 * the audit links a list row carries are built from them. This front end is
 * mounted under /r, so the browser's address bar reads /r/app/... while every
 * call to the server reads /app/...
 *
 * Two functions rather than one, and the distinction matters:
 *
 *   appPath is for anything the client router handles — a Link's `to`, a
 *   navigate call. The router already carries the mount point as its basename
 *   and prepends it, so adding it here as well produces /r/r/app/... which
 *   resolves to the application shell loading itself inside its own route. That
 *   was the first defect this port produced, and it produced it everywhere at
 *   once, which is the argument for having one function rather than inline
 *   string concatenation at sixty call sites.
 *
 *   absolutePath is for a full page load: signing out, switching role, or
 *   bouncing to the access page when a session has expired. Those leave the
 *   router behind, so the mount point has to be in the URL.
 */
// The live build is served under /r by the platform or by nginx. The static
// build is served from wherever its folder sits and routes on the hash, so it
// has no mount path of its own: prefixing one would produce links that work on
// the machine the build was made on and nowhere else.
export const MOUNT = IS_STATIC ? '' : '/r'

/** A platform path as a route for the client router. The router adds the
 *  mount point itself, so this normalises and does not prepend it. */
export function appPath(route: string): string {
  if (!route) return '/app'
  if (MOUNT && route.startsWith(`${MOUNT}/`)) return route.slice(MOUNT.length)
  if (MOUNT && route === MOUNT) return '/app'
  return route.startsWith('/') ? route : `/${route}`
}

/** A platform path as a URL for a full page load.
 *
 *  In the static build that is a hash, because the folder may be served from a
 *  subdirectory and a server that knows nothing about the application cannot
 *  rewrite a deep path back to index.html. */
export function absolutePath(route: string): string {
  const path = appPath(route)
  return IS_STATIC ? `#${path}` : `${MOUNT}${path}`
}

/** The router's current path as the platform path to ask the server about.
 *  useLocation already strips the basename, so this only normalises. */
export function platformPath(pathname: string): string {
  const trimmed = MOUNT && pathname.startsWith(MOUNT)
    ? pathname.slice(MOUNT.length)
    : pathname
  const cleaned = trimmed.replace(/\/+$/, '')
  return cleaned || '/app'
}
